import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import { useEventSource } from '@/composables/useEventSource'
import type { User, EnvConfig } from '@/types/auth'

interface TokenResponse {
  access_token: string
  refresh_token: string
  id_token: string
  expires_in: number
  provisioningUser: {
    username: string
    firstName: string
    lastName: string
    email: string
    roles: string
    profileIds: string[]
    accountId: string
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isAuthenticated = ref<boolean>(false)
  const entitlements = ref<string[]>([])
  const logoutReason = ref<string>('')
  
  let refreshTimeout: ReturnType<typeof setTimeout> | null = null
  let initialized = false
  // Refresh ahead of the token's real expiry (reported by Identity as expires_in) instead of a
  // fixed interval, which drifted out of sync whenever the actual token lifetime was shorter.
  const REFRESH_BUFFER_MS = 60 * 1000
  const MIN_REFRESH_DELAY_MS = 5 * 1000
  const LOGOUT_REQUEST_TIMEOUT_MS = 5 * 1000
  
  const isAdmin = computed<boolean>(() => {
    return user.value?.authorities?.includes('ROLE_ADMIN') ?? false
  })
  
  const profileIds = computed<string[]>(() => user.value?.profileIds ?? [])
  const companyId = computed<string | null>(() => user.value?.companyId ?? null)
  
  const initializeAuth = (): void => {
    if (initialized) return
    initialized = true

    const isLoggedIn = localStorage.getItem('isLoggedIn')
    if (isLoggedIn === 'true') {
      const storedUser = localStorage.getItem('authenticatedUser')
      if (storedUser) {
        try {
          user.value = JSON.parse(storedUser)
          isAuthenticated.value = true

          const expiry = user.value?.exp || Number(localStorage.getItem('expiry'))
          if (expiry && Date.now() > expiry) {
            logout('Session expired')
          } else {
            fetchEntitlements()
            scheduleTokenRefresh(expiry)
            useEventSource().connect()
          }
        } catch (error) {
          console.error('Error restoring authenticated user:', error)
          logout('Session expired')
        }
      }
    }
  }
  
  const authenticate = (): void => {
    const env = getEnvConfig()
    const scopes = 'openid atcc profile email offline_access avatax avatax_api'
    const redirectUri = `${window.location.protocol}//${window.location.host}/login/callback`
    const url = `${env.AVALARA_IDENTITY_BASE_URL}/connect/authorize?response_type=code&client_id=${env.AVALARA_IDENTITY_CLIENT_ID}&redirect_uri=${redirectUri}&scope=${scopes}`
    window.location.href = url
  }
  
  const handleAuthCallback = async (code: string): Promise<boolean> => {
    try {
      const redirectUrl = `${window.location.protocol}//${window.location.host}`
      const response = await api.get<TokenResponse>(`/app/v1/authentication/token/${code}?redirectUrl=${redirectUrl}`, {
        skipAuth: true
      } as any)
      
      const data = response.data
      const expiry = Date.now() + (data.expires_in * 1000)
      
      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('refresh_token', data.refresh_token)
      localStorage.setItem('id_token', data.id_token)
      localStorage.setItem('expiry', String(expiry))
      localStorage.setItem('account_id', data.provisioningUser.accountId)
      localStorage.setItem('isLoggedIn', 'true')
      
      const userInfo = data.provisioningUser
      user.value = {
        username: userInfo.username,
        firstname: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        authorities: userInfo.roles.split(',').map((role: string) => role.trim()),
        profileIds: userInfo.profileIds,
        companyId: userInfo.accountId,
        token: data.id_token,
        refresh_token: data.refresh_token,
        exp: expiry
      }
      
      localStorage.setItem('authenticatedUser', JSON.stringify(user.value))
      localStorage.setItem('isAdmin', String(isAdmin.value))
      isAuthenticated.value = true
      
      // Log login
      await api.post('/app/v1/profiles/login')
      
      // Connect SSE and start refresh cycle
      useEventSource().connect()
      scheduleTokenRefresh(expiry)
      
      // Fetch entitlements
      await fetchEntitlements(true)
      
      return true
    } catch (error) {
      console.error('Authentication callback error:', error)
      throw error
    }
  }
  
  /**
   * Shared by concurrent callers. Every request that comes back 401 asks the interceptor to refresh,
   * so a page load with ten requests in flight fired ten identical refresh calls. They all wait on
   * the same attempt now.
   */
  let refreshInFlight: Promise<void> | null = null

  const refreshToken = async (): Promise<void> => {
    if (refreshInFlight) return refreshInFlight

    refreshInFlight = performTokenRefresh().finally(() => {
      refreshInFlight = null
    })
    return refreshInFlight
  }

  const performTokenRefresh = async (): Promise<void> => {
    const refreshTokenValue = localStorage.getItem('refresh_token')

    // getItem returns null when the key is absent, and interpolating that into the URL sent the
    // literal string "null" to the gateway. There is nothing to refresh with, so skip the doomed
    // round trip instead of reading the resulting 502 as an upstream outage.
    if (!refreshTokenValue) {
      console.error('Token refresh skipped: no refresh token stored')
      logout('Session expired')
      return
    }

    try {
      // skipAuth: the request interceptor would otherwise attach the current (possibly already
      // expired) id_token as the Authorization header. The backend's OAuth2 resource server
      // rejects an expired bearer token with 401 before this permitAll route is even reached,
      // which meant a perfectly valid refresh_token still ended in a forced logout.
      const response = await api.get<TokenResponse>(`/app/v1/authentication/token/refresh/${refreshTokenValue}`, {
        skipAuth: true
      } as any)

      const data = response.data
      const expiry = Date.now() + (data.expires_in * 1000)
      
      localStorage.setItem('access_token', data.access_token)
      localStorage.setItem('id_token', data.id_token)
      localStorage.setItem('expiry', String(expiry))
      localStorage.setItem('refresh_token', data.refresh_token)
      
      if (user.value) {
        user.value.token = data.id_token
        user.value.exp = expiry
        localStorage.setItem('authenticatedUser', JSON.stringify(user.value))
      }
      
      // Reconnect SSE with new token
      useEventSource().reconnect()
      scheduleTokenRefresh(expiry)
      
      // Refresh entitlements
      await fetchEntitlements(true)
    } catch (error) {
      console.error('Token refresh error:', error)
      logout('Session expired')
    }
  }
  
  const scheduleTokenRefresh = (expiry: number): void => {
    if (refreshTimeout) clearTimeout(refreshTimeout)
    const delay = Math.max(expiry - Date.now() - REFRESH_BUFFER_MS, MIN_REFRESH_DELAY_MS)
    refreshTimeout = setTimeout(refreshToken, delay)
  }
  
  const fetchEntitlements = async (force: boolean = false): Promise<string[]> => {
    if (!force && entitlements.value.length > 0) {
      return entitlements.value
    }
    
    try {
      const response = await api.get<string[]>('/app/v1/users/entitlements')
      entitlements.value = response.data
      return entitlements.value
    } catch (error) {
      console.error('Error fetching entitlements:', error)
      entitlements.value = []
      return []
    }
  }
  
  const hasEntitlement = (entitlement: string): boolean => {
    return entitlements.value.includes(entitlement)
  }
  
  /**
   * One logout per page. Clearing the session re-renders the page the user was on, its requests
   * come back 401, and the interceptor asks for a logout of its own. That second call found no
   * id_token and sent the browser to /login, cancelling the Identity sign-out still in flight, so
   * the Identity session survived and /login signed straight back in.
   */
  let logoutInFlight: Promise<void> | null = null

  const logout = (reason: string = ''): Promise<void> => {
    if (!logoutInFlight) logoutInFlight = performLogout(reason)
    return logoutInFlight
  }

  const performLogout = async (reason: string): Promise<void> => {
    logoutReason.value = reason

    const env = getEnvConfig()
    let token: string | null = null

    // Everything in this block is best-effort cleanup. None of it may throw past this
    // function, or the redirect in `finally` below — the actual fix for "logout doesn't
    // send you back to the login screen" — would never run.
    try {
      token = localStorage.getItem('id_token')

      // Disconnect SSE
      useEventSource().disconnect()

      // Clear refresh timer
      if (refreshTimeout) {
        clearTimeout(refreshTimeout)
        refreshTimeout = null
      }

      // Log logout (ignore auth handling/refresh logic for this call). Bounded, because the
      // redirect waits on it: a call that never answers would leave the user signed in.
      try {
        await api.post('/app/v1/profiles/logout', undefined, {
          skipAuthHandling: true,
          timeout: LOGOUT_REQUEST_TIMEOUT_MS
        } as any)
      } catch (e) {
        // Ignore logout API errors
      }

      // Clear state
      user.value = null
      isAuthenticated.value = false
      entitlements.value = []

      // Clear localStorage
      localStorage.setItem('isLoggedIn', 'false')
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('expiry')
      localStorage.removeItem('id_token')
      localStorage.removeItem('account_id')
      localStorage.removeItem('authenticatedUser')
      localStorage.removeItem('isAdmin')
    } catch (error) {
      console.error('Error during logout cleanup:', error)
    } finally {
      // Picked up by App.vue's pageshow handler so a bfcache/back-button restore after this
      // navigation still lands the user on the login screen instead of a stale authenticated view.
      sessionStorage.setItem('logged_out', 'true')

      const loginUrl = `${window.location.origin}/login`
      const logoutUrl = token
        ? `${env.AVALARA_IDENTITY_BASE_URL}/connect/endsession?${new URLSearchParams({
          id_token_hint: token,
          post_logout_redirect_uri: loginUrl
        })}`
        : loginUrl
      window.location.href = logoutUrl
    }
  }
  
  const getEnvConfig = (): EnvConfig => {
    const defaults = {
      AVALARA_IDENTITY_BASE_URL: import.meta.env.VITE_AVALARA_IDENTITY_BASE_URL,
      AVALARA_IDENTITY_CLIENT_ID: import.meta.env.VITE_AVALARA_IDENTITY_CLIENT_ID,
      ENV: import.meta.env.VITE_ENV,
      CLASSIFY_EMBED_SESSION_URL: import.meta.env.VITE_CLASSIFY_EMBED_SESSION_URL || '/app/v1/classifier/embed-session',
      CLASSIFY_EMBED_PUBLIC_ORIGIN: import.meta.env.VITE_CLASSIFY_EMBED_PUBLIC_ORIGIN,
      FVS_URL: import.meta.env.VITE_FVS_URL,
      FVS_CONFIGURATION_ID: import.meta.env.VITE_FVS_CONFIGURATION_ID,
      FVS_CALLBACK_URL: import.meta.env.VITE_FVS_CALLBACK_URL
    }

    // First try window.__env (for nginx injection), then fall back to Vite env
    if (window.__env) {
      return {
        ...defaults,
        ...window.__env
      }
    }
    return defaults
  }
  
  return {
    user,
    isAuthenticated,
    isAdmin,
    profileIds,
    companyId,
    entitlements,
    logoutReason,
    initializeAuth,
    authenticate,
    handleAuthCallback,
    refreshToken,
    fetchEntitlements,
    hasEntitlement,
    logout,
    getEnvConfig
  }
})
